import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { PublicClientApplication, EventType, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalConfig } from './authConfig';
import { FakeMsalInstance } from './testing/fakeMsalInstance';

import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/index.css';

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 *
 * E2E runs set `window.__E2E_MOCK_AUTH__` before this module evaluates (via
 * Playwright's `page.addInitScript`, see `e2e/support/mockAuth.ts`) so tests can
 * drive the app as an already-authenticated user without a real Azure tenant.
 * This never happens outside of a Playwright-controlled page.
 */
const e2eMockAuth = typeof window !== 'undefined' ? window.__E2E_MOCK_AUTH__ : undefined;
const msalInstance = e2eMockAuth
    ? new FakeMsalInstance(e2eMockAuth.account)
    : new PublicClientApplication(msalConfig);

const REDIRECT_PATHNAME = '/redirect';

// If the user hits /redirect directly (no auth response in URL), normalize back to /.
// Do NOT do this when auth params are present, or MSAL won't be able to process the response.
const hasAuthResponseParams =
    window.location.search.includes('code=') ||
    window.location.search.includes('error=') ||
    window.location.hash.includes('code=') ||
    window.location.hash.includes('error=');

if (window.location.pathname === REDIRECT_PATHNAME && !hasAuthResponseParams) {
    window.history.replaceState({}, document.title, '/');
}

const setActiveAccountFromCache = () => {
    if (msalInstance.getActiveAccount()) {
        return;
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
        // Account selection logic is app dependent. Adjust as needed for different use cases.
        msalInstance.setActiveAccount(accounts[0]);
    }
};

const trySilentSso = async () => {
    if (msalInstance.getActiveAccount()) {
        return;
    }

    // If an account exists in cache, prefer activating that over attempting silent SSO.
    if (msalInstance.getAllAccounts().length > 0) {
        return;
    }

    try {
        const response = await msalInstance.ssoSilent({
            scopes: ['openid', 'profile'],
        });

        if (response?.account) {
            msalInstance.setActiveAccount(response.account);
        }
    } catch (error) {
        // Silent SSO is optional; if it can't be done without interaction, just continue unsigned.
        if (!(error instanceof InteractionRequiredAuthError)) {
            console.debug('Silent SSO not available at startup.', error);
        }
    }
};

const startApp = async () => {
    // Required for @azure/msal-browser v3 before any cache/account APIs are reliable.
    await msalInstance.initialize();

    // Listen for sign-in event and set active account
    msalInstance.addEventCallback((event) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
            const account = event.payload.account;
            msalInstance.setActiveAccount(account);
        }
    });

    // Process any pending redirect response. If none exists, this returns null.
    const redirectResponse = await msalInstance.handleRedirectPromise();
    if (redirectResponse?.account) {
        msalInstance.setActiveAccount(redirectResponse.account);
    } else {
        setActiveAccountFromCache();
        await trySilentSso();
    }

    const root = createRoot(document.getElementById('root'));
    root.render(<App instance={msalInstance} />);
};

startApp().catch((error) => {
    console.error('MSAL initialization failed:', error);

    // Render the app anyway so users can still attempt interactive sign-in.
    const root = createRoot(document.getElementById('root'));
    root.render(<App instance={msalInstance} />);
});
